// FILE: src/pages/AdminComplianceReportPage.jsx
// Compliance reports (plan WP-8, MTO 11.1): every student's credited time,
// course status, final score and support requests, with a CSV download of
// exactly the fields the table shows (B-7: the export used to read fields
// the payload doesn't have and came out blank). Untimed (reviewer) accounts
// are not part of the compliance set; the server leaves them out.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  LinearProgress,
  Link,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { fetchComplianceSummary } from "../features/complianceSlice";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../components/ui";
import { CourseStatusChip, ResponsiveTable } from "../components/admin/AdminBits";
import { downloadCsv, formatDate, personName } from "../components/admin/format";
import { formatDuration } from "../utils/timeFormat";
import { userStatusLabel } from "../i18n/labels";
import { useCan } from "../hooks/usePermissions";

const minutes = (m) => Math.max(0, Number(m) || 0);
const statusKey = (s) => String(s || "not started").toLowerCase().replace(/\s+/g, "-");

/** One table/CSV row from the GET /api/admin/compliance-report/all payload. */
export function complianceRow(item) {
  const profile = item?.profile || {};
  const hours = item?.hours || {};
  const tickets = Array.isArray(item?.tickets) ? item.tickets : [];
  const status = statusKey(item?.courseStatus);
  const score = Number(item?.averageFinalScore);
  return {
    id: profile._id ? String(profile._id) : "",
    name: personName(profile, "Name not given"),
    firstName: profile.firstName || "",
    lastName: profile.lastName || "",
    email: profile.email || "",
    registrationNumber: profile.registrationNumber || "",
    registrationDate: profile.registrationDate || null,
    inClassMinutes: minutes(hours.inClassMinutes),
    homeLinkMinutes: minutes(hours.homeLinkMinutes),
    totalMinutes: minutes(hours.totalTimeSpent ?? minutes(hours.inClassMinutes) + minutes(hours.homeLinkMinutes)),
    status,
    score: status === "completed" && Number.isFinite(score) ? Math.round(score) : null,
    openTickets: tickets.filter((t) => t?.status !== "RESOLVED").length,
    allTickets: tickets.length
  };
}

export function complianceCsv(rows) {
  return [
    [
      "Student ID",
      "First name",
      "Last name",
      "Email",
      "Registration date",
      "Chapter minutes",
      "Homelink minutes",
      "Total minutes",
      "Course status",
      "Average final score (%)",
      "Open support requests",
      "All support requests"
    ],
    ...rows.map((r) => [
      r.id,
      r.firstName,
      r.lastName,
      r.email,
      r.registrationDate ? new Date(r.registrationDate).toISOString().slice(0, 10) : "",
      r.inClassMinutes,
      r.homeLinkMinutes,
      r.totalMinutes,
      userStatusLabel(r.status),
      r.score ?? "",
      r.openTickets,
      r.allTickets
    ])
  ];
}

export default function AdminComplianceReportPage() {
  // Downloads need records.export (RBAC); reading the report records.view.
  const canExport = useCan("records.export");
  const dispatch = useDispatch();
  const { summary, loading, error } = useSelector((state) => state.compliance);
  const [query, setQuery] = useState("");

  const load = useCallback(() => dispatch(fetchComplianceSummary()), [dispatch]);
  useEffect(() => {
    load();
  }, [load]);

  // Students only: administrators have no course record.
  const rows = useMemo(
    () =>
      (Array.isArray(summary) ? summary : [])
        .filter((item) => item?.profile && item.profile.role !== "admin" && !item.untimed)
        .map(complianceRow)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [summary]
  );
  const q = query.trim().toLowerCase();
  const shown = q ? rows.filter((r) => `${r.name} ${r.email} ${r.registrationNumber}`.toLowerCase().includes(q)) : rows;

  const exportCsv = () => {
    downloadCsv(`compliance-report-${new Date().toISOString().slice(0, 10)}.csv`, complianceCsv(shown));
  };

  return (
    <Box>
      <PageHeader
        title="Compliance reports"
        description="Each student's credited time, course status, final score and support requests, for Ministry records."
        actions={
          canExport ? (
            <Button
              variant="contained"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportCsv}
              disabled={shown.length === 0}
            >
              {q ? `Download CSV (${shown.length} shown)` : "Download CSV"}
            </Button>
          ) : undefined
        }
      />

      <Alert severity="info" sx={{ mb: 2.5 }}>
        Untimed (reviewer) accounts and administrators aren't part of the compliance set, so they aren't listed here or in
        the download.
      </Alert>

      <Section id="compliance" title="Students" contentSx={{ p: { xs: 2, sm: 3 } }}>
        {error && rows.length === 0 ? (
          <ErrorState error={error} title="We couldn't load the compliance report" onRetry={load} />
        ) : loading && rows.length === 0 ? (
          <LoadingState variant="table" rows={5} label="Loading the compliance report…" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={AssessmentOutlinedIcon}
            title="No student records yet"
            description="Students appear here once they've been added. Add one from the Students page."
            action={
              <Button component={RouterLink} to="/admin/users" variant="outlined">
                Go to students
              </Button>
            }
          />
        ) : (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 1, alignItems: { sm: "center" } }}>
              <TextField
                id="compliance-search"
                label="Find a student by name, email or registration number"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ flex: 1, maxWidth: 520 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon aria-hidden />
                      </InputAdornment>
                    )
                  }
                }}
              />
              <Typography role="status" sx={{ color: "text.secondary" }}>
                {q ? `${shown.length} of ${rows.length} students` : `${rows.length} ${rows.length === 1 ? "student" : "students"}`}
              </Typography>
            </Stack>
            <Box sx={{ height: 8, mb: 1 }}>{loading && <LinearProgress aria-label="Updating the report" />}</Box>
            {error && <ErrorState compact error={error} onRetry={load} />}
            {shown.length === 0 ? (
              <EmptyState compact title="No students match" description="Check the spelling or search for part of the name or email." />
            ) : (
              <ResponsiveTable label="Compliance report" minWidth={980}>
                <TableHead>
                  <TableRow>
                    <TableCell component="th" scope="col">
                      Student
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Chapter time
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Homelink time
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Course status
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Final score
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Open requests
                    </TableCell>
                    <TableCell component="th" scope="col" align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((r) => (
                    <TableRow key={r.id || r.email} hover>
                      <TableCell component="th" scope="row">
                        {r.id ? (
                          <Link component={RouterLink} to={`/admin/users/${r.id}`} sx={{ display: "block", fontWeight: 600 }}>
                            {r.name}
                          </Link>
                        ) : (
                          <Typography component="span" sx={{ display: "block", fontWeight: 600 }}>
                            {r.name}
                          </Typography>
                        )}
                        <Typography component="span" sx={{ display: "block", color: "text.secondary", overflowWrap: "anywhere" }}>
                          {r.email}
                          {r.registrationDate ? ` · registered ${formatDate(r.registrationDate)}` : ""}
                        </Typography>
                        {r.registrationNumber && (
                          <Typography component="span" sx={{ display: "block", color: "text.secondary" }}>
                            Registration number {r.registrationNumber}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {formatDuration(r.inClassMinutes * 60)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {formatDuration(r.homeLinkMinutes * 60)}
                      </TableCell>
                      <TableCell>
                        <CourseStatusChip status={r.status} />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {r.score === null ? (
                          <Typography component="span" sx={{ color: "text.secondary" }}>
                            {r.status === "completed" ? "—" : "Not finished"}
                          </Typography>
                        ) : (
                          `${r.score}%`
                        )}
                      </TableCell>
                      <TableCell align="right">{r.openTickets}</TableCell>
                      <TableCell align="right">
                        {r.id ? (
                          <Button
                            component={RouterLink}
                            to={`/admin/compliance/${r.id}`}
                            variant="outlined"
                            aria-label={`View the record for ${r.name}`}
                          >
                            View record
                          </Button>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ResponsiveTable>
            )}
          </>
        )}
      </Section>
    </Box>
  );
}
